import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { PercentageInput, PercentageInputProps } from '../../UI';
import { contributionFieldValuesFromForm, incomePair, isContributionFieldMissing } from '../contributionPairs';
import { useManualContributionEntered } from '../hooks/useManualContributionEntered';
import { InvestmentFormValues } from '../types';

type InvestmentRecurringContributionPercentageInputProps = Pick<PercentageInputProps, 'label' | 'className'>;

export const InvestmentRecurringContributionPercentageInput = ({
  label,
  className,
}: InvestmentRecurringContributionPercentageInputProps) => {
  const { t: tForm } = useTranslation('form');
  const { control, setValue, clearErrors } = useFormContext<InvestmentFormValues>();
  // Disabled only once the manual side (amount) is in use — a blank form leaves it editable.
  const manualContributionEntered = useManualContributionEntered();
  const { field, fieldState } = useController<InvestmentFormValues, 'recurringContributionPercentage'>({
    control,
    name: 'recurringContributionPercentage',
    rules: {
      validate: (percentage, formValues) => {
        // 0/blank means "no percentage"; only a real out-of-range value is a range error (the
        // input already caps entry at 0–100, so this is a backstop).
        if (percentage !== null && percentage !== undefined && (percentage < 0 || percentage > 100)) {
          return false;
        }
        // Income pair is "both or neither": a linked source needs a percentage.
        return isContributionFieldMissing(incomePair, 'percentage', contributionFieldValuesFromForm(formValues))
          ? tForm('investment-percentage-required-for-income')
          : true;
      },
    },
  });

  return (
    <PercentageInput
      {...field}
      onChange={(percentage) => {
        field.onChange(percentage);
        // Clearing the percentage blanks the derived amount/frequency (meaningless without a
        // complete link), mirroring the income-clear direction; the income source is left as a
        // straggler for validation to catch.
        if (percentage === null || percentage === 0) {
          setValue('recurringContribution.amount', null);
          setValue('recurringContribution.frequency', null);
        }
        // Clear standing contribution errors as the user edits — they re-check on the next save
        // rather than nagging live (validation is submit-only, see useInvestmentForm).
        clearErrors([
          'incomeSourceID',
          'recurringContributionPercentage',
          'recurringContribution.amount',
          'recurringContribution.frequency',
        ]);
      }}
      className={className}
      inputProps={{ readOnly: manualContributionEntered }}
      status={manualContributionEntered ? 'disabled' : fieldState.error ? 'errored' : 'normal'}
      helperText={fieldState.error?.message}
      label={label ?? tForm('investment-recurring-contribution-percentage-input-label')}
    />
  );
};
